import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { X, Calendar, Clock, Users, MapPin, Plus } from 'lucide-react';

const DAYS_OF_WEEK = [
    { key: 'monday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.monday' },
    { key: 'tuesday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.tuesday' },
    { key: 'wednesday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.wednesday' },
    { key: 'thursday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.thursday' },
    { key: 'friday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.friday' },
    { key: 'saturday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.saturday' },
    { key: 'sunday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.sunday' }
];

export default function AddRecurrenceModal({ isOpen, course, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        recurrence_type: 'weekly',
        recurrence_days: [],
        start_date: new Date().toISOString().slice(0, 10),
        end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10), // 30 giorni da oggi
        start_time: course.standard_start_time?.slice(0, 5) || '09:00',
        end_time: course.standard_end_time?.slice(0, 5) || '10:00',
        location: '',
        notes: ''
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        if (error) setError(null);
    };

    const handleDayToggle = (dayKey) => {
        setFormData(prev => ({
            ...prev,
            recurrence_days: prev.recurrence_days.includes(dayKey)
                ? prev.recurrence_days.filter(d => d !== dayKey)
                : [...prev.recurrence_days, dayKey]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.recurrence_days.length === 0) {
            setError('Seleziona almeno un giorno della settimana');
            return;
        }
        
        if (new Date(formData.start_date) >= new Date(formData.end_date)) {
            setError('La data di fine deve essere successiva alla data di inizio');
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const api = getAuthenticatedApi();
            
            // Crea lezioni manualmente usando l'endpoint create_session
            const sessionsCreated = [];
            const startDate = new Date(formData.start_date);
            const endDate = new Date(formData.end_date);
            
            const dayMap = {
                'monday': 1,
                'tuesday': 2,
                'wednesday': 3,
                'thursday': 4,
                'friday': 5,
                'saturday': 6,
                'sunday': 0
            };
            
            let currentDate = new Date(startDate);
            
            while (currentDate <= endDate) {
                const dayOfWeek = currentDate.getDay();
                
                // Controlla se questo giorno è tra quelli selezionati per le lezioni
                const shouldCreate = formData.recurrence_days.some(dayName => 
                    dayMap[dayName] === dayOfWeek
                );
                
                if (shouldCreate) {
                    const sessionDate = currentDate.toISOString().slice(0, 10);
                    const startDateTime = `${sessionDate} ${formData.start_time}:00`;
                    const endDateTime = `${sessionDate} ${formData.end_time}:00`;
                    
                    try {
                        const response = await api.post('/gym_courses.php?action=create_session', {
                            course_id: course.id,
                            session_date: sessionDate,
                            start_time: formData.start_time, // Invia solo HH:MM, l'API aggiunge :00
                            end_time: formData.end_time,     // Invia solo HH:MM, l'API aggiunge :00
                            max_participants: course.max_participants,
                            location: formData.location || null,
                            notes: formData.notes || null
                        });
                        
                        if (response.data.success) {
                            sessionsCreated.push(response.data.session);
                        }
                    } catch (err) {
                        console.error(`Errore creazione sessione per ${sessionDate}:`, err);
                    }
                }
                
                currentDate.setDate(currentDate.getDate() + 1);
            }
            
            const response = {
                data: {
                    success: true,
                    sessions_created: sessionsCreated.length
                }
            };
            
            if (response.data.success) {
                const sessionsCount = response.data.sessions_created || 0;
                toast.success(t('gym.courses.addRecurrenceModal.messages.success', { count: sessionsCount }));
                onSuccess();
            } else {
                throw new Error(response.data.error || t('gym.courses.addRecurrenceModal.messages.createError'));
            }
            
        } catch (err) {
            console.error('Errore aggiunta lezioni:', err);
            setError(err.response?.data?.error || err.message || t('gym.courses.addRecurrenceModal.messages.createError'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t('gym.courses.addRecurrenceModal.title', { courseTitle: course.title })}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
                            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Giorni della Settimana */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-3">
                            {t('gym.courses.addRecurrenceModal.fields.daysLabel')}
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {DAYS_OF_WEEK.map(day => (
                                <label
                                    key={day.key}
                                    className="flex items-center gap-2 p-3 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.06] cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={formData.recurrence_days.includes(day.key)}
                                        onChange={() => handleDayToggle(day.key)}
                                        className="w-4 h-4 text-indigo-600 rounded"
                                    />
                                    <span className="text-sm text-gray-900 dark:text-white">{t(day.labelKey)}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Date */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Calendar className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.startDateLabel')}
                            </label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                required
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Calendar className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.endDateLabel')}
                            </label>
                            <input
                                type="date"
                                name="end_date"
                                value={formData.end_date}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                required
                            />
                        </div>
                    </div>

                    {/* Orari */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Clock className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.startTimeLabel')}
                            </label>
                            <input
                                type="time"
                                name="start_time"
                                value={formData.start_time}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                required
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Clock className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.endTimeLabel')}
                            </label>
                            <input
                                type="time"
                                name="end_time"
                                value={formData.end_time}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                required
                            />
                        </div>
                    </div>

                    {/* Location e Note */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <MapPin className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.locationLabel')}
                            </label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                placeholder={t('gym.courses.addRecurrenceModal.fields.locationPlaceholder')}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Users className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.notesLabel')}
                            </label>
                            <input
                                type="text"
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder={t('gym.courses.addRecurrenceModal.fields.notesPlaceholder')}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Anteprima */}
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                            {t('gym.courses.addRecurrenceModal.preview.title')}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-white/65">
                            {t('gym.courses.addRecurrenceModal.preview.description', { 
                                startDate: formData.start_date, 
                                endDate: formData.end_date 
                            })}
                        </p>
                        {formData.recurrence_days.length > 0 && (
                            <p className="text-sm text-gray-600 dark:text-white/65 mt-1">
                                {t('gym.courses.addRecurrenceModal.preview.days', { 
                                    days: formData.recurrence_days.map(day => 
                                        t(DAYS_OF_WEEK.find(d => d.key === day)?.labelKey || '')
                                    ).join(', ')
                                })}
                            </p>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-white/85 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg"
                        >
                            {t('gym.courses.addRecurrenceModal.actions.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading || formData.recurrence_days.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <Plus size={16} />
                            )}
                            {t('gym.courses.addRecurrenceModal.actions.create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
